import {
  CommunicationAccount,
  CommunicationClient,
  CommunicationPortal,
  DevicePlatform,
  EmployeeListSortOption,
  EmployeeSearchSortOption,
  EmploymentStatus,
  EnrollmentOption,
  FetchState,
  FundingOption,
  HsabPlanType,
  LoginMethod,
  PageableState,
  PartnerFormDocumentResponse, HsabPayee,
  SftpMapping,
  SftpVendorTypeOption,
  SortOrder,
  UserHsaAccountStatus,
  HsabPayeeLastActive,
  HsabAccountNametag,
  UserInterestRate,
  CardProviderOption,
  Payor
} from "@/shared/types";

import moment from "moment";
import { BankCustodian } from "@/admin/types";

export interface EmployeeContributionsState extends PageableState {
  updateFetchState: FetchState;
  getFetchState: FetchState;
  payrollDate: string;
  payrollEditCutOffDateTime: string;
  employeeContributions: EmployeeContribution[];
  selectedEmployeeId: string;
  searchDropdownList: EmployeeSearchDropdownItem[];
  isBeforePriorYearCutOff: boolean;
  isInNextYearProcessDateWindow: boolean;
}

export interface EmployeeContributionsUpdateResponse {
  liftAmount: number;
  liftLimit: number;
  contributionLimit: number;
  resultCode: string;
  message: number;
  employerAmount: number;
  isEmployeeModified: boolean;
  employerModifiedBy: { firstName: string; lastName: string; };
}

export interface EmployeeContributionsListResult {
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
  nextPayrollDate: string;
  nextPayrollEditCutOffDateTime: string;
  employeeContributions: EmployeeContribution[];
}

export interface EmployeeContributionRequest {
  employeeId: number;
  employeeAmount: number;
  liftAmount: number;
  employerAmount: number;
  canUpdateEmployeeCurrentAndFuture: boolean;
}

export interface EmployeeContribution {
  contributionLimit: number;
  employeeId: number;
  employeeFirstName: string;
  employeeLastName: string;
  employeeAmount: number;
  liftAmount: number;
  employerAmount: number;
  liftLimit: number;
  lastModified: string;
  hasChange: boolean;
  ytdContributions: number;
  countRemainingPayrollDates: number;
  totalEmployerAmountRemaining: number;
  isEmployeeModified: boolean;
  employerModifiedBy: { firstName: string; lastName: string; };
  modifiedByPayrollRecalculation: boolean;
  isEmployeeCurrentFuturePayrollUpdate: false;
}

export interface ContributionParameters {
  pageNumber: number;
  pageSize: number;
  employerId: number;
  searchTerm: string;
}

export interface Contact {
  userId: number;
  name: string;
  phone: string;
  email: string;
}

export interface ProfileWizard {
  generalInformation: ProfileWizardStatus;
  planDetail: ProfileWizardStatus;
  payrollInformation: ProfileWizardStatus;
  currentStep: ProfileWizardSteps;
}

export interface ProfileWizardStatus {
  complete: boolean;
  step: ProfileWizardSteps;
}

export enum ProfileWizardSteps {
  None,
  GeneralInformation,
  PlanDetail,
  PayrollSetup,
  Finish
}

export interface ProfileDictionary {
  [employerId: number]: ProfileEmployer;
}

export interface ProfileEmployer {
  employerInformationState: FetchState;
  employerInformation: ProfileInformation;
  bankInformation: EmployerAccounts;
  bankInformationState: FetchState;
  payrollInformationState: FetchState;
  payrollInformation: PayrollInformation;
  planDetailInformationState: FetchState;
  planDetailInformation: PlanDetailInformation[];
  isPlanDateLocked: boolean;
  profileWizardState: FetchState;
  profileWizard: ProfileWizard;
  enrollmentMetrics: DashboardEnrollment;
  enrollmentState: FetchState;
  isCurrentPlanEditLocked: boolean;
  fundingOption: FundingOption;
  fundingOptionHistory: FundingOption[];
  fundingOptionHistoryState: FetchState;
  enrollmentOption: EnrollmentOption;
  enrollmentOptionState: FetchState;
  wexEmployerProfile: WexEmployerProfile;
}

export interface ProfileState extends ProfileEmployer {
  allEmployers: ProfileDictionary;
}

export enum PayPeriodType {
  Weekly = "Weekly",
  BiWeekly = "Bi-Weekly",
  Monthly = "Monthly",
  SemiMonthly = "Semi-Monthly"
}

export interface Employee {
  employerId: number;
  employeeId: number;
  externalEmployeeId: string;
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  dateOfBirth: string;
  ssn: string;
  accountTypeEffectiveDate: string;
  effectiveDate: string;
  accountType: string;
  email: string;
  isEnrolled: boolean;
  terminationDate: string;
  hsaEligibilityEndDate: string;
  annualElection: number;
  canResendInvite: boolean;
  hsaIntervals: EmployeeHsaInterval[];
  employmentStatus?: EmploymentStatus;
  enrollmentStatus?: EnrollmentStatus;
}

export interface UserEmploymentInfo {
  employerName: string;
  customEmployerId: string;
  employerSpecifiedId: string;
  employeeIdRef: string;
  dateOfHire: string;
  employmentStatus: EmploymentStatus;
  statusEffectiveDate: string;
  terminationDate: string;
  wexEmployerCode: string;
}

export interface EmploymentState {
  employmentInfo: UserEmploymentInfo;
  fetchState: FetchState;
  username: string;
}

export interface PayrollSummaryToDate {
  fetchState: FetchState;
  employeeKey?: string;
  employer: {
    pending: number,
    processed: number
  };
  employee: {
    pending: number,
    processed: number
  };
}

export interface EmployeeHsaInterval {
  startDate: string;
  endDate: string;
}

export interface EmployeeState {
  getFetchState: FetchState;
  pages: Employee[][];
  totalRecords: number;
  pageSize: number;
  sortOrder: SortOrder;
  sortOption: EmployeeListSortOption;
  pageNumber: number;
  searchDropdownList: EmployeeSearchDropdownItem[];
}

export interface FileUploadRequest {
  effectiveYear: number | null;
  file: File;
}

export interface EmployeeSearchDropdownItem {
  employeeId: number;
  externalEmployeeId: string;
  firstName: string;
  lastName: string;
}

export interface EmployerInviteDateState {
  EmployerInviteDateResultState: FetchState;
  earliestInviteDateMomentUtc: moment.Moment;
  result: EmployerInviteDateResult;
}

export interface EmployerInviteDateResult {
  earliestInviteSendDate: string; // the no sooner date of BEND-66
  initialEmailSentDate: string; // date the first invitation email batch was marked complete
  isEmployerActivated: boolean; // has the employer setup been confirmed by bank
}

export interface EmployerInviteDateRequest {
  earliestInviteSendDate: string;
}

export interface EmployerScheduledInviteRequest {
  employerId: number;
  sendOn: string;
}

export interface EmployeeListResult {
  employees: Employee[];
  pageSize: number;
  pageNumber: number;
  totalRecords: number;
}

export interface PlanDetailsInformation {
  isDateLocked?: boolean;
  isCurrentPlanEditLocked?: boolean;
  employerPlanDetails: PlanDetailInformation[];
}

export interface PlanDetailInformation {
  planDetailId: number;
  employerId: number;
  planStartDate: string;
  planEndDate: string;
  contribution: ContributionEmployer;
}

export interface ContributionEmployer {
  doesEmployerContributeToHsa: boolean;
  individualContributionAmountToHsa: number;
  familyContributionAmountToHsa: number;
  doesEmployerContributeUpFront: boolean;
  upfrontContributionPercent: number;
  areContributionsProrated: boolean;
}

export enum PayrollValidationMessageType {
  None = "none",
  Warning = "warning",
  DateChange = "dateChange"
}

export interface PayrollValidationMessage {
  message: string;
  messageType: PayrollValidationMessageType;
}

export interface PayrollCalendarState {
  byYear: PayrollDatesInYear[];
  validationFetchState: FetchState;
  employerId?: number;
  validationMessages: PayrollValidationMessage[];
}

export interface PayrollDatesInYear {
  year: number;
  payrollDates: PayrollCalendarDate[];
  pristinePayrollDates: PayrollCalendarDate[];
  fetchState: FetchState;
  isDirty: boolean;
}

export enum DateGenerationCode {
  None = "none",
  BankHoliday = "bankHoliday",
  Weekend = "weekend",
  ManuallyAdjusted = "manuallyAdjusted"
}

export interface PayrollCalendarDate {
  date: string;
  dateGenerationCode: DateGenerationCode;
}

export interface PayrollInformation {
  employerId: number;
  payrollProviderName: string;
  payrollCutOffDays: number;
  payrollCycle: string;
  dayOfWeek: string;
  firstDateOfYear: string;
  firstSemiMonthlyDay: number;
  secondSemiMonthlyDay: number;
  dayOfMonth: number;
  contact: Contact;
  nextPayrollDate: string;
  nextPayrollCutOffDate: string;
  payrollCalendarLockEndDate: string;
  fundingOption: FundingOption;
}

export interface PayrollReportState {
  fetchState: FetchState;
  payrollDates: PayrollReportDates;
  automatedFundingLeaveDate: string;
  automatedFundingLeaveDateFetchState: FetchState;
}

export interface PayrollReportDates {
  reportDates: ReportDate [];
  nextPayrollCutOffDate: string;
}

export enum EligibilityStatus {
  None = "none",
  NoAccount = "noAccount",
  Ineligible = "ineligible",
  Eligible = "eligible"
}

export enum EnrollmentStatus {
  None = "none",
  Ineligible = "ineligible",
  NotStarted = "notStarted",
  InProgress = "inProgress",
  Enrolled = "enrolled",
  AccountClosed = "accountClosed",
  Blocked = "blocked"
}

export interface PayrollEmployee {
  employeeId: number;
  eligibilityStatus: EligibilityStatus;
}

export interface PayrollEmployeeResponse {
  employees: PayrollEmployee[];
}

export interface ReportDate {
  payDate: string;
  isDownloadReady: boolean;
}

export interface CompanyDetails {
  profileInformation: ProfileInformation;
  bankInformation: EmployerAccounts;
  fundingOption: FundingOption;
  enrollmentOption: EnrollmentOption;
}

export interface ProfileInformation {
  employerId: number;
  partnerId: number;
  externalEmployerId: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  websiteUrl: string;
  mainContactPhone: string;
  employeeCount: string;
  taxId: string;
  bankCustodian: BankCustodian;
  allowedEmployerBankCustodians: BankCustodian[];
  isEnrollmentDisabled: boolean;
}

export interface BankAccount {
  bankRoutingNumber: string;
  bankAccountNumber: string;
}

export interface EmployerAccounts {
  fundingAccount: BankAccount;
  feeAccount: BankAccount;
  authorizationDate: string;
}

export interface EmployerAccountsUpdate {
  fundingAccount: BankAccount;
  feeAccount: BankAccount;
  authorize: boolean;
}

export interface DashboardState {
  fetchState: FetchState;
  dashboard: EmployerDashboard;
}

export interface PayrollDetail {
  payrollDate: string;
  employerPayrollCutOffDate: string;
  employeePayrollCutOffDate: string;
  payrollContributionTotal: number;
}

export interface PayrollDashboardState {
  employeeContributions: EmployeeConributionResponseState;
  upcomingPayrolls: PayrollDetail [];
  upcomingPayrollFetchState: FetchState;
  payrollContributionsGraph: PayrollContributionsGraphState;
}

export interface EmployeeConributionResponseState {
  fetchState: FetchState;
  year: number;
  planStartDate: string;
  employeeContributionRecords: {[year: number]: EmployeeContributionDetail};
}

export interface EmployeeContributionDetail {
  employeeContributions: PayrollAmount [];
  processedAmount: number;
  totalAmount: number;
  year: number;
  planStartDate: string;
}

export interface PayrollAmount {
  payrollDate: string;
  amount: number;
}

export interface EmployerDashboard {
  enrollment: DashboardEnrollment;
}

export interface DashboardEnrollment {
  enrolled: number;
  inProgress: number;
  notStarted: number;
  percentageEnrolled: number;
  total: number;
  pendingInvitations: number;
}

export interface PayrollContributionsGraphState extends PayrollContributionsGraph {
  fetchState: FetchState;
  page: number;
}

export interface PayrollContributionsGraph {
  canNavigateForward: boolean;
  canNavigateBackward: boolean;
  entries: PayrollContributionsGraphEntry[];
}

export interface PayrollContributionsGraphEntry {
  date: string;
  employeeAmount: number;
  employerAmount: number;
  isProjected: boolean;
}

export interface EmployeePlanYearContributionState extends EmployeePlanYearContributionResponse {
  fetchState: FetchState;
  pages: PlanYearContributionPage[];
  employeeId: number;
}

export interface PlanYearContributionPage {
  pageNumber: number;
  contributions: EmployeePayrollContribution [];
}

export interface EmployeePlanYearContributionResponse {
  pageNumber: number;
  pageSize: number;
  totalContributionCount: number;
  contributions: EmployeePayrollContribution [];
}

export interface EmployeePayrollContribution {
  payrollDate: string;
  employeeOneTimeAmount: number;
  employeeRecurringAmount: number;
  employerAmount: number;
}
export interface EmployeeConributionHistorySummaryResponse {
  employerContributedAmount: number;
  employerScheduledAmount: number;
  employeeContributedAmount: number;
  employeeScheduledAmount: number;
}

export interface EmployeeConributionHistorySummaryState extends EmployeeConributionHistorySummaryResponse {
  fetchState: FetchState;
  employeeId: number;
}

export interface EmployeeConributionHistoryState {
  summary: EmployeeConributionHistorySummaryState;
  payrollHistory: EmployeePlanYearContributionState;
}

export enum PartnerEmployerListSortOption {
  None = 0,
  Name = 1,
  Enrolled = 2,
  CensusPartner = 3,
  ExternalEmployerId = 4,
}

export interface PartnerState {
  fetchState: FetchState;
  partner: Partner;
  totalRecords: number;
  queryTime: number;
  maxScore: number;
  employerSearchTypeAheadResult: EmployerTypeAheadResponse;
  employerSearchTypeAheadFetchState: FetchState;
}

export interface Partner {
  partnerId: number;
  employers: PartnerEmployer[];
}

export interface PartnerEmployer {
  employerId: number;
  name: string;
  status: PartnerEmployerStatus;
  partnerId?: number;
  fundingOption: FundingOption;
  hasFundingOptionHistory: boolean;
  externalEmployerId: string;
  censusManager: CensusManager;
  enrolledEmployeeCount: number;
  isTerminated: boolean;
  taxId: string;
  enrollmentOption: EnrollmentOption;
  score: number;
}

export interface PartnerEmployersSearchFields {
  employerName: string;
  employerId: string;
  taxId: string;
  externalEmployerId: string;
}

export interface Communication {
  id: number;
  startDate: string;
  endDate?: string;
  displayOrder: number;
  subject: string;
  message: string;
  actionText: string;
  actionLink: string;
  isActionLinkExternal: boolean;
  displayToIndividuals: boolean;
  isEmployerExclusionList: boolean;
  applyToAllEmployers: boolean;
  imageName: string;
  portal: CommunicationPortal;
  iconClass: string;
  accountType: HsabPlanType;
  communicationAccount: CommunicationAccount;
  client: CommunicationClient;
  employerGroups: CommunicationEmployerGroup[];
}
export interface CommunicationEmployerGroup {
  id: number;
  name: string;
  code: string;
}

export interface EmployerTypeAheadResponse {
  employers: CommunicationEmployerGroup[];
}

export interface PartnerEmployerList {
  tooManyEmployers: boolean;
  employers: EmployerListItem[];
}
export interface EmployerListItem {
  id: number;
  name: string;
}
export interface SaveCommunication {
  success: boolean;
  id: number;
}

export enum PartnerEmployerStatus {
  None = "none",
  UmbAccountCreationComplete = "umbAccountCreationComplete",
  EmployerOnboardingComplete = "employerOnboardingComplete",
  EmployeeInviteEmailComplete = "employeeInviteEmailComplete",
  Suspended = "suspended"
}

export interface PartnerEmployersResponse {
  employers: PartnerEmployer[];
}
export interface EmployeeSearchResultDetail {
  userId: number;
  name: string;
  firstName: string;
  lastName: string;
  login: string;
  lastFourSsn: string;
  phoneNumber: string;
  partnerName: string;
  externalPartnerId: string;
  emailAddress: string;
  employeeId: string;
  employer: string;
  employerId: number;
  username: string;
  enrolled: boolean;
  hsaBankAccountNumber: string;
  investmentAccountNumbers: string[];
  investmentAccountTypes: InvestmentAccountType[];
}

export interface EmployeeSearchTerm {
  firstName: string;
  lastName: string;
  email: string;
  login: string;
  ssn4: string;
  accountNumber: string;
  investmentAccountNumber: string;
  employerEmployeeId: string;
  externalEmployerId: string;
  sortOrder: SortOrder;
  sortOption: EmployeeSearchSortOption;
}

export interface EmployeeSearchResults {
  employees: EmployeeSearchResultDetail[];
  searchTerm: EmployeeSearchTerm;
  numberFound: number;
  elapsedMilliseconds: number;
  maxScore: number;
  error: string;
}

export interface EmployeeSearchState {
  searchEmployeeState: FetchState;
  searchResult: EmployeeSearchResults;
  selectedPartnerId: number;
  searchTerm: EmployeeSearchTerm;
  selectedPageNumber: number;
}

export enum InvestmentAccountType {
  None = "none",
  Advisory = "advisory",
  RiaManaged = "riaManaged",
  OpenBrokerage = "openBrokerage"
}

export enum UploadStatus {
  Processing = "processing",
  Success = "success",
  Fail = "fail",
  Cancelled = "cancelled",
  PartialSuccess = "partialSuccess",
  Rejected = "rejected"
}

export interface CancelContributionHoldsRequest  {
  contributionHoldIds: number[];
}

export interface FileCancelResponse {
  isSuccess: boolean;
  id: number;
}

export interface UploadError {
  lineNumber: number;
  message: string;
}

export interface PriorYearInfoResponse {
  isBeforePriorYearCutOff: boolean;
}

export interface NextYearProcessDateInfoResponse {
  isInNextYearProcessDateWindow: boolean;
}

export interface FundingFileSummaryResponse {
  files: ContributionUploadedFile[];
  totalRecords: number;
  isUploadDisabled: boolean;
}

export enum CensusUploadStatus {
  None = "none",
  Fail = "fail",
  SomeErrors = "someErrors",
  NoErrors = "noErrors",
  Pending = "pending"
}

export enum CensusUploadType {
  None = "none",
  Web = "web",
  Sftp = "sftp",
  EmployeeNavigator = "employeeNavigator"
}

export interface EmployerCensusFile {
  employerCensusFileId: number;
  fileName: string;
  uploadDate: string;
  uploadTime: string;
  uploadStatus: CensusUploadStatus;
  uploadType: CensusUploadType;
  newUserCount: number;
  updatedUserCount: number;
  unchangedUserCount: number;
  errorCount: number;
}

// UserService/UserService.Contracts/V1/EmployerCensusFile.cs
export interface EmployerCensusFileApi {
  employerCensusFileId: number;
  employerId: number;
  fileName: string;
  dateUploaded: string;
  uploadedBy: string;
  uploadStatus: CensusUploadStatus;
  censusUploadType: CensusUploadType;
  newEmployees: number;
  updatedEmployees: number;
  unchangedEmployees: number;
  errorCount: number;
}

export interface EmployerCensusFileState {
  fileFetchState: FetchState;
  fileDetailsFetchState: FetchState;
  selectedFileId: number;
  files: EmployerCensusFile[];
  pages: EmployerCensusFile[][];
  fileDetails: EmployerCensusFile[];
  showHowItWorks: boolean;
  totalRecords: number;
  pageNumber: number;
  hasPendingFile: boolean;
}

export interface EmployerCensusFileResponse {
  files: EmployerCensusFileApi[];
  totalRecords: number;
  hasPending: boolean;
}

export interface EmployerCensusFileDetailsResponse {
  file: EmployerCensusFileApi;
}

export interface EmployeeIdFile {
  employeeIdFileId: number;
  employerId: number;
  fileName: string;
  dateUploaded: string;
  uploadStatus: CensusUploadStatus;
  uploadedBy: string;
  updatedUserCount: number;
}

export interface EmployeeIdFileState {
  fileFetchState: FetchState;
  files: EmployeeIdFile[];
  pages: EmployeeIdFile[][];
  totalRecords: number;
  pageNumber: number;
}
export interface EmployeeIdFileResponse {
  files: EmployeeIdFile[];
  totalRecords: number;
  hasPending: boolean;
}
export interface EmployeeListSearchFields {
  firstname: string;
  lastname: string;
  employeeId: string;
}
export interface ContributionUploadedFile {
  id: number;
  fileName: string;
  date: string;
  payrollDate: string;
  uploadDate: string;
  uploadTime: string;
  status: UploadStatus;
  canCancel: boolean;
  fileDetails: ContributionFileDetail [];
  totalAchAmount: number;
  hasMultipleUploads: boolean;
  contributionEffectiveYear: number;
}

export interface ContributionFileDetail {
  id: number;
  fileName: string;
  date: string;
  payrollDate: string;
  uploadDate: string;
  uploadTime: string;
  status: UploadStatus;
  canCancel: boolean;
  achAmount: number;
  contributionEffectiveYear: number;
}

export interface ContributionUploadedFileDetails {
  file: ContributionUploadedFile;
  errors: UploadError[];
  numberOfRecords: number;
  employerTotal: number;
  employeePreTaxTotal: number;
  employeePostTaxTotal: number;
  contributionsTotal: number;
  onHoldTotal: number;
  includedInACHTotal: number;
  errorRowCount: number;
}

export interface ContributionUploadedFileState {
  fileFetchState: FetchState;
  fileDetailsFetchState: FetchState;
  selectedFileId: number;
  selectedFilePayrollDate: string;
  files: ContributionUploadedFile[];
  pages: ContributionUploadedFile[][];
  fileDetails: ContributionUploadedFileDetails[];
  showHowItWorks: boolean;
  pendingContributionsState: PendingContributionsState;
  totalRecords: number;
  pageNumber: number;
  isUploadDisabled: boolean;
}

export interface PendingContributionsState {
  fetchState: FetchState;
  totalRecords: number;
  pageSize: number;
  pageNumber: number;
  pages: PendingContributionModel[][];
  hasExpiredContributions: boolean;
}

export interface PendingContributionReport {
  pendingContributions: PendingContributionModel[];
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
  hasExpiredContributions: boolean;
}

export interface PendingContributionModel extends PendingContribution {
  groupedContributions?: PendingContribution[];
}

export interface PendingContribution {
  payrollDate: string;
  employeeName: string;
  employeeId: string;
  employerAmount: number;
  employeeAmount: number;
  total: number;
  contributionHoldId: number;
  isExpired: boolean;
}

export interface Holiday {
  date: string;
  name: string;
}

export interface BankHolidaysInYear {
  year: number;
  fetchState: FetchState;
  holidays: string[];
}

export interface BankHolidayState {
  byYear: BankHolidaysInYear[];
}

export interface EmployeeListSortParameters {
  sortOrder: SortOrder;
  sortOption: EmployeeListSortOption;
  reset: boolean;
}

export enum FileUploadProcessingRule {
  override = "override",
  multipleFiles = "multipleFiles"
}

export enum FileUploadErrorProcessingRule {
  forceEntireSuccess = "forceEntireSuccess",
  allowPartialSuccess = "allowPartialSuccess"
}

export interface FileProcessorSettingsState {
  fetchState: FetchState;
  fileProcessorRule: FileUploadProcessingRule;
  errorHandlingRule: FileUploadErrorProcessingRule;
  sftpVendorTypeFetchState: FetchState;
  sftpMappingFetchState: FetchState;
  sftpVendorType: {[employerId: number]: SftpVendorTypeOption};
  sftpMappings: {[employerId: number]: SftpMapping[]};
}

export interface FileProcessorSettingsRequest {
  fileProcessorRule: FileUploadProcessingRule;
  errorHandlingRule: FileUploadErrorProcessingRule;
}

export interface FileProcessSettingsResponse extends FileProcessorSettingsRequest {
  errorMessage: string;
}

export enum CensusManager {
  none =  "none",
  bend = "bend",
  employeeNavigator = "employeeNavigator"
}

export interface UpdateCensusManagerRequest {
  censusManager: CensusManager;
}

export interface BillingHistoryState {
  cognitoUsername: string;
  yearSummaryDictionary: {
    [key: number]: BillingHistoryYearSummary
  };
  years: number[];
  yearsFetchState: FetchState;
  selectedYear: number;
}

export interface BillingHistoryYearSummary {
  year: number;
  fetchState: FetchState;
  months: BillingHistoryMonth[];
}

export interface BillingHistoryMonth {
  billingPeriodStartDate: string;
  averageCashBalance: number;
  averageInvestmentBalance: number;
  feeWaiverThreshold: number;
  feeWaiverType: FeeWaiverType;
  administratorFee: number;
  payerType: PayerType;
  feeExclusionType: BillingFeeExclusionType;
  employerId?: number;
}

export enum PayerType {
  None = "none",
  Employer = "employer",
  Employee = "employee",
  Individual = "individual"
}

export enum BillingFeeExclusionType {
  None = "none",
  BankAccount = "bankAccount",
  FutureEligibility = "futureEligibility",
  Termination = "termination",
  BlockedEmployer = "blockedEmployer",
  DuplicateEmployers = "duplicateEmployers",
  AverageBalanceThreshold = "averageBalanceThreshold",
  BendPartnerConditional = "bendPartnerConditional",
  FutureFeeStartDate = "futureFeeStartDate",
  ClosedAccount = "closedAccount"
}

export enum FeeWaiverType {
  None = "none",
  Balance = "balance",
  BalanceAndInvestment = "balanceAndInvestment"
}

export interface FundingOptionRequest {
  fundingOption: FundingOption;
}

export interface EnrollmentOptionRequest {
  enrollmentOption: EnrollmentOption;
}

export enum EmailHistoryEventSource {
  AWS = "AWS",
  Okta = "Okta"
}

export interface EmailHistoryEvent {
  emailHistoryId: number;
  sesMessageId: string;
  subject: string;
  fromAddress: string;
  eventType: string;
  emailAddress: string;
  diagnosticCode: string;
  bounceType: string;
  eventTimestamp: string;
  rawEvent: string;
  source: EmailHistoryEventSource;
}

export interface EmailHistoryState {
  fetchState: FetchState;
  history: EmailHistoryEvent[];
  email: string;
  rawEventDictionary: {
    [key: number]: EmailHistoryRawEvent
  };
}

export interface EmailHistoryRawEvent {
  fetchState: FetchState;
  data: string;
}

export const devicePlatformMap: Record<DevicePlatform, string> = {
  unknown: "Unknown",
  browser: "Browser",
  ios: "iOS",
  android: "Android",
  windows: "Windows",
  macOS: "macOS",
  linux: "Linux",
  chromeOS: "ChromeOS"
};

export interface LoginHistoryEvent {
  loginHistoryId: number;
  timestamp: string;
  platform: DevicePlatform;
  application: string;
  appVersion: string;
  method: LoginMethod;
  ipAddress: string;
  isMobile: boolean;
}

export interface LoginHistoryResponse {
  loginHistory: LoginHistoryEvent[];
}

export interface LoginHistoryState {
  fetchState: FetchState;
  history: LoginHistoryEvent[];
}

export interface BankAccountHoldState {
  username: string;
  holdsFetchState: FetchState;
  holdTypesFetchState: FetchState;
  defaultAccountId: number;
  accountHolds: BankAccountHoldsModel[];
  holdTypeModels: HoldTypeModel[];
  isVersionConflict: boolean;
  holdHistoryExportFetchState: FetchState;
}

export enum HoldType {
  None = "none",
  ReturnedWelcomeKit = "returnedWelcomeKit",
  HighRisk = "highRisk",
  OFAC = "ofac",
  DeathPayout = "deathPayout",
  FDIC = "fdic",
  DeathmasterHit = "deathmasterHit",
  NoTransactions = "noTransactions",
  CreditsOnly = "creditsOnly",
  Levies = "levies",
  NegativeBalance = "negativeBalance",
  ExcessContribution = "excessContribution",
  AccountReopen = "accountReopen",
  Fraud = "fraud",
  NoInterest = "noInterest",
  NoInvestments = "noInvestments",
  CIP = "cip",
  ConsumerMove = "consumerMove"
}

export interface BankAccountHoldsResponseModel {
  defaultAccountId?: number;
  holdsByAccount: BankAccountHoldsModel[];
}

export interface HoldAccountInformationModel {
  accountId: number;
  accountNumber: string;
  accountHoldVersion: number;
  accountStatus: UserHsaAccountStatus;
}

export interface BankAccountHoldsModel {
  account: HoldAccountInformationModel;
  activeHolds: BankAccountHoldModel[];
}

export interface BankAccountHoldModel {
  holdType: HoldType;
  dateCreated: string;
  dateLastModified: string;
  scheduledReleaseDate: string;
  dateHoldRemoved: string;
  createdBy: string;
  modifiedBy: string;
}

export interface HoldTypeModel {
  holdTypeId: HoldType;
  holdDescription: string;
  disallowedFunctions: BankAccountHoldFunctionType[];
  isWexOriginated: boolean;
}

export interface BankAccountHoldFormModel {
  holdType: HoldType;
  dateCreated: string;
  dateLastModified: string;
  scheduledReleaseDate?: string;
  dateHoldRemoved: string;
  createdBy: string;
  modifiedBy: string;
  holdTypeId: HoldType;
  holdDescription: string;
  disallowedFunctions: BankAccountHoldFunctionType[];
  isActive: boolean;
  isWexOriginated: boolean;
}

export enum BankAccountHoldFunctionType {
  None = "none",
  Deposits = "deposits",
  Interest = "interest",
  Withdrawals = "withdrawals",
  TaxForms = "taxForms",
  Reopen = "reopen",
  Fees = "fes",
  OnlineAccess = "onlineAccess",
  InvestmentTransactions = "investmentTransactions"
}

export interface BankAccountHoldUpdateRequest {
  accountHoldVersion: number;
  holdNote: string;
  accountHolds: BankAccountHoldUpdateModel[];
}

export interface BankAccountHoldUpdateModel {
  holdType: HoldType;
  isActive: boolean;
  scheduledReleaseDate?: string;
}

export interface PayeeStoreState {
  payeesForUser: HsabPayee[];
  fetchState: FetchState;
  createState: FetchState;
  saveState: FetchState;
  deleteState: FetchState;
  lastActivePayees: HsabPayeeLastActive[];
}

export interface ResourceFormState {
  fetchStates: Record<number, FetchState>;
  resourceFormData: Record<number, PartnerFormDocumentResponse>;
}

export interface UserNoteModel {
  note: string;
  createdDate: Date;
  createdBy: string;
}

export interface UserNoteState {
  fetchState: FetchState;
  notes: UserNoteModel[];
  username: string;
}

export interface UserNotesResponse {
  notes: UserNoteModel[];
}

export interface HsabAccountState {
  fetchState: FetchState;
  account: HsabAccountNametag;
  username: string;
  wexLink: string;
}

export interface InterestRateState {
  fetchState: FetchState;
  username: string;
  interestRates: UserInterestRate[];
}

export interface CardProviderOptionsState {
  fetchState: FetchState;
  username: string;
  cardProviderOptions: CardProviderOption;
}

export interface EmployerFeeScheduleState {
  fetchState: FetchState;
  username: string;
  employerFeeSchedules: EmployerFeeSchedule[];
}

export interface EmployerFeeScheduleResponse {
  employerFeeSchedules: EmployerFeeSchedule[];
}

export interface EmployerFeeSchedules {
    employerFeeSchedules: EmployerFeeSchedule[];
}

export interface EmployerFeeSchedule {
    feeName: string;
    feeAmount: number;
    balanceWaiver: number;
    whoPays: Payor;
}

export interface Metric {
    name: string;
    categoryId?: string;
}

export interface Category {
    name: string;
    crossSellTitle: string;
    crossSellImageAltText: string;
    crossSellDescription: string;
    crossSellMoreInfoUrl: string;
    crossSellImageUrl: string;
}

export interface ProductPlanTab extends Category {
    showTab: boolean;
    showMetrics: boolean;
    metrics: Metric[];
    loaded?: boolean;
    navigated?: boolean;
}

export interface TabComponent {
  tabTitle: string;
  showTab?: boolean;
  active?: boolean;
}

export interface EmployerProfileRequest {
    employerCode: string;
    wexInstanceCode: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zip: string;
}

export interface EmployerProfileResponse {
    adminAlias: string;
    employerCode: string;
    messageId: string;
    employerProfile: WexEmployerProfile;
    employerStatus: string;
    employerCancelDate?: string;
    operationResult: OperationResult;
}

export interface WexEmployerProfile {
    employerCode: string;
    employerName: string;
    employerEIN: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zip: string;
    numberOfEmployees?: string;
    parentEmployerCode?: string;
}

export interface EmployerProfileUpdateResponse {
    adminAlias: string;
    employerCode: string;
    messageId: string;
    operationResult: OperationResult;
}

export interface ExecutionMessage {
    notificationType: string;
    code: number;
    description: string;
}

export interface OperationResult {
    operationSucceeded: boolean;
    executionMessages: ExecutionMessage[];
}